import { useRef, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  X, CloudArrowUp, CheckCircle, Spinner, MapPin, Clock, ChartLineUp,
  Warning, FileText,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Phase 53 — Shipment-scoped Logger Import
// Lightweight wrapper around POST /api/logger-imports/shipments/{id}/upload
// and POST /api/logger-imports/{import_id}/confirm. The shipment is pre-bound
// and locked; AI candidate scoring is bypassed; window filtering is exposed
// as a single checkbox.
// ---------------------------------------------------------------------------
export default function ShipmentLoggerImportDialog({ shipment, onClose, onAttached }) {
  const fileRef = useRef(null);
  const [phase, setPhase] = useState("upload"); // upload | preview | replaying | attached
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [importDoc, setImportDoc] = useState(null);
  const [windowFilter, setWindowFilter] = useState(true);
  const [windowPreview, setWindowPreview] = useState(null);
  const [editingWindow, setEditingWindow] = useState(false);
  const [windowDraft, setWindowDraft] = useState({ start: "", end: "" });
  const [savingWindow, setSavingWindow] = useState(false);
  const [replay, setReplay] = useState(null);
  const [delivering, setDelivering] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/logger-imports/shipments/${shipment.id}/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportDoc(res.data);
      setWindowPreview(res.data.window_preview);
      // Default the window-filter toggle ON if the shipment has a start_time;
      // otherwise it would filter nothing and we'd rather use the full file.
      setWindowFilter(!!res.data.window_preview?.start_time);
      setPhase("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logger upload failed");
    } finally {
      setUploading(false);
    }
  };

  const refreshPreview = async (nextFlag) => {
    setWindowFilter(nextFlag);
    if (!importDoc) return;
    try {
      const r = await axios.get(
        `${API}/logger-imports/${importDoc.id}/window-preview`,
        { params: { shipment_id: shipment.id } },
      );
      setWindowPreview(r.data);
    } catch (e) { void e; }
  };

  // Convert ISO string ↔ datetime-local input value (YYYY-MM-DDTHH:mm).
  const isoToLocal = (iso) => (iso ? iso.slice(0, 16) : "");
  const localToIso = (local) => {
    if (!local) return null;
    // datetime-local has no timezone; assume UTC for consistency with how
    // the rest of the platform stores timestamps.
    return new Date(local + "Z").toISOString();
  };

  const openWindowEditor = () => {
    setWindowDraft({
      start: isoToLocal(windowPreview?.start_time),
      end:   isoToLocal(windowPreview?.end_time),
    });
    setEditingWindow(true);
  };

  const saveWindow = async () => {
    if (!windowDraft.start) {
      toast.error("Start time is required");
      return;
    }
    if (windowDraft.end && windowDraft.end < windowDraft.start) {
      toast.error("End time cannot be earlier than start time");
      return;
    }
    setSavingWindow(true);
    try {
      // Reuse the canonical shipment update endpoint — no parallel workflow.
      await axios.put(`${API}/shipments/${shipment.id}`, {
        start_time: localToIso(windowDraft.start),
        end_time:   windowDraft.end ? localToIso(windowDraft.end) : null,
      });
      // Refresh the preview counts immediately.
      const r = await axios.get(
        `${API}/logger-imports/${importDoc.id}/window-preview`,
        { params: { shipment_id: shipment.id } },
      );
      setWindowPreview(r.data);
      setEditingWindow(false);
      toast.success("Shipment window updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update shipment window");
    } finally {
      setSavingWindow(false);
    }
  };

  const windowEditDisabled =
    ["cancelled", "failed", "quarantined"].includes(shipment.status);

  const handleConfirm = async () => {
    setConfirming(true);
    setPhase("replaying");
    try {
      const res = await axios.post(
        `${API}/logger-imports/${importDoc.id}/confirm`,
        { shipment_id: shipment.id, window_filter: windowFilter },
      );
      setReplay(res.data?.summary || res.data);
      setPhase("attached");
      toast.success("Logger replayed for this shipment");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirm failed");
      setPhase("preview");
    } finally {
      setConfirming(false);
    }
  };

  const handleMarkDelivered = async () => {
    setDelivering(true);
    try {
      await axios.post(`${API}/shipments/${shipment.id}/deliver`);
      toast.success("Shipment marked as delivered");
      if (onAttached) onAttached();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark as delivered");
    } finally {
      setDelivering(false);
    }
  };

  const handleDone = () => {
    if (onAttached) onAttached();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !uploading && !confirming) onClose(); }}>
      <Card className="bg-zinc-950 border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5"
        data-testid="shipment-logger-import-dialog"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-zinc-100 flex items-center gap-2">
              <CloudArrowUp size={16} weight="duotone" className="text-sky-400" /> Import Logger
            </h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Attaching to <span className="text-zinc-300 font-mono">{shipment.name}</span>
              {" "}· <span className="text-zinc-500 font-mono">#{shipment.id?.slice(0, 8)}</span>
            </p>
          </div>
          <button onClick={onClose} disabled={uploading || confirming}
            className="text-zinc-500 hover:text-zinc-200" data-testid="shipment-import-close">
            <X size={18} />
          </button>
        </div>

        {/* Locked shipment chip */}
        <div className="bg-sky-500/5 border border-sky-500/30 rounded-sm px-3 py-2 mb-4 text-[11px] text-sky-200 flex items-center gap-2">
          <CheckCircle size={12} weight="duotone" className="text-sky-300" />
          Shipment is pre-selected. Imported readings will be associated only with this shipment.
        </div>

        {phase === "upload" && (
          <div className="space-y-3">
            {shipment.lifecycle_state && shipment.lifecycle_state !== "active" && (
              <div
                className="bg-amber-500/10 border border-amber-500/40 rounded-sm px-3 py-2.5 text-[11px] text-amber-100 space-y-2"
                data-testid="logger-draft-guardrail-banner"
              >
                <div className="flex items-start gap-2">
                  <Warning size={13} weight="duotone" className="text-amber-300 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-semibold uppercase tracking-wider text-amber-200 text-[10px]">
                      Shipment is not active ({shipment.lifecycle_state})
                    </div>
                    <div className="mt-1 leading-relaxed">
                      You can still attach this logger — readings, alerts, and audit
                      trail will be captured. However, the post-import notification
                      summary (email/SMS) will be <b>held</b> until the shipment is
                      activated.
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => { onClose(); }}
                    className="text-[10px] font-mono px-2 py-1 rounded border border-teal-600/60 text-teal-200 hover:bg-teal-500/10"
                    data-testid="logger-draft-activate-now-btn"
                  >
                    Activate now, then attach
                  </button>
                  <span className="text-[10px] text-amber-300/80">
                    or use the dropzone below to <b>attach anyway</b> — summary held.
                  </span>
                </div>
              </div>
            )}
            <div className="border-2 border-dashed border-zinc-800 rounded-sm p-8 text-center hover:border-sky-500/40 transition-colors cursor-pointer"
              onClick={() => fileRef.current?.click()}
              data-testid="shipment-import-drop">
              <CloudArrowUp size={32} weight="duotone" className="mx-auto text-zinc-600 mb-2" />
              <p className="text-xs text-zinc-300">Click to choose a logger CSV file</p>
              <p className="text-[10px] text-zinc-500 mt-1">Single-file upload, single-shipment attach</p>
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="shipment-import-file-input" />
            {uploading && (
              <div className="flex items-center gap-2 text-[11px] text-sky-300 font-mono">
                <Spinner size={12} className="animate-spin" /> Parsing logger file…
              </div>
            )}
          </div>
        )}

        {phase === "preview" && importDoc && (
          <div className="space-y-4" data-testid="shipment-import-preview">
            {/* File stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <PreviewTile icon={<FileText size={11} className="text-zinc-400" />}
                label="File" value={importDoc.filename?.slice(0, 24) || "—"} />
              <PreviewTile icon={<ChartLineUp size={11} className="text-emerald-400" />}
                label="Total readings" value={windowPreview?.total ?? 0} />
              <PreviewTile icon={<MapPin size={11} className={windowPreview?.has_gps ? "text-emerald-400" : "text-zinc-500"} />}
                label="GPS route" value={windowPreview?.has_gps ? "Yes" : "No"} />
              <PreviewTile icon={<Clock size={11} className="text-sky-400" />}
                label="Span"
                value={`${(windowPreview?.first_timestamp || "—").slice(5, 16)} → ${(windowPreview?.last_timestamp || "—").slice(5, 16)}`} />
            </div>

            {/* Window filter toggle + counts */}
            <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-3 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={windowFilter}
                  onChange={(e) => refreshPreview(e.target.checked)}
                  className="mt-0.5"
                  data-testid="shipment-import-window-toggle" />
                <div className="flex-1">
                  <div className="text-xs text-zinc-100 font-medium">Use this shipment window</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    Only replay readings whose timestamps fall within{" "}
                    <span className="font-mono text-zinc-300">
                      {windowPreview?.start_time?.slice(0, 19) || "—"}
                    </span>
                    {" "}→{" "}
                    <span className="font-mono text-zinc-300">
                      {windowPreview?.end_time?.slice(0, 19) || "open"}
                    </span>
                    . Outside-window readings are staged on the import record but never produce alerts.
                  </div>
                </div>
              </label>

              {windowPreview?.shipment_open && windowFilter && (
                <div className="flex items-center gap-1.5 text-[10px] text-amber-300 mt-1">
                  <Warning size={10} weight="duotone" />
                  Shipment is still open (no end_time). Every reading after start_time is treated as in-window.
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 mt-1">
                <PreviewMini label="In window"
                  value={windowFilter ? (windowPreview?.in_window ?? 0) : (windowPreview?.total ?? 0)}
                  accent="text-emerald-300" />
                <PreviewMini label="Before start" value={windowPreview?.before_start ?? 0}
                  accent={(windowPreview?.before_start || 0) > 0 ? "text-amber-300" : "text-zinc-500"} />
                <PreviewMini label="After end" value={windowPreview?.after_end ?? 0}
                  accent={(windowPreview?.after_end || 0) > 0 ? "text-amber-300" : "text-zinc-500"} />
              </div>

              {/* Inline shipment-window editor (Phase 53b). Lightweight — uses
                  the canonical PUT /shipments/{id} endpoint. After save, the
                  window-preview is re-fetched and counts update in place. */}
              <div className="pt-1.5 border-t border-zinc-800/80">
                {!editingWindow ? (
                  <button onClick={openWindowEditor}
                    disabled={windowEditDisabled}
                    className="text-[10px] text-teal-300 hover:text-teal-200 underline underline-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="ship-import-edit-window-btn"
                    title={windowEditDisabled ? `Cannot edit window for '${shipment.status}' shipment` : "Adjust the shipment window"}>
                    Adjust shipment window
                  </button>
                ) : (
                  <div className="space-y-2" data-testid="ship-import-window-editor">
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <span className="text-[9px] uppercase tracking-wider text-zinc-500">Start</span>
                        <input type="datetime-local" value={windowDraft.start}
                          onChange={(e) => setWindowDraft((d) => ({ ...d, start: e.target.value }))}
                          className="mt-0.5 w-full bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1 text-[11px] font-mono text-zinc-100"
                          data-testid="ship-import-window-start" />
                      </label>
                      <label className="block">
                        <span className="text-[9px] uppercase tracking-wider text-zinc-500">End (optional)</span>
                        <input type="datetime-local" value={windowDraft.end}
                          onChange={(e) => setWindowDraft((d) => ({ ...d, end: e.target.value }))}
                          className="mt-0.5 w-full bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1 text-[11px] font-mono text-zinc-100"
                          data-testid="ship-import-window-end" />
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={saveWindow} disabled={savingWindow}
                        className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-[11px] h-7 font-semibold"
                        data-testid="ship-import-window-save">
                        {savingWindow ? "Saving…" : "Save & refresh"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingWindow(false)}
                        disabled={savingWindow}
                        className="border-zinc-700 hover:bg-zinc-800 text-[11px] h-7"
                        data-testid="ship-import-window-cancel">
                        Cancel
                      </Button>
                      <span className="text-[10px] text-zinc-500 italic">
                        Logger replay is not started — only the window is updated.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
              <Button variant="outline" size="sm" onClick={onClose}
                className="border-zinc-700 hover:bg-zinc-800 text-xs"
                data-testid="shipment-import-cancel">Cancel</Button>
              <Button size="sm" onClick={handleConfirm} disabled={confirming}
                className="bg-sky-500 hover:bg-sky-400 text-zinc-950 text-xs font-semibold"
                data-testid="shipment-import-confirm">
                <CheckCircle size={11} weight="bold" className="mr-1" />
                Confirm &amp; Replay
              </Button>
            </div>
          </div>
        )}

        {phase === "replaying" && (
          <div className="py-10 text-center">
            <Spinner size={32} className="animate-spin mx-auto text-sky-400 mb-2" />
            <div className="text-sm text-zinc-200">Replaying logger through alert engine…</div>
            <div className="text-[11px] text-zinc-500 mt-1">In-app alerts are written per row; one summary email/SMS will fire at the end.</div>
          </div>
        )}

        {phase === "attached" && replay && (
          <div className="space-y-4" data-testid="shipment-import-attached">
            <div className="text-center py-2">
              <CheckCircle size={36} weight="duotone" className="text-emerald-400 mx-auto mb-1.5" />
              <div className="text-zinc-100 font-semibold text-base">Logger Attached</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                {replay.points_replayed ?? 0} points replayed · {replay.alerts_generated ?? 0} alerts created
              </div>
            </div>

            {replay.delivery_suggestion?.eligible && (
              <div className="rounded-sm border border-teal-500/30 bg-teal-500/5 p-3"
                data-testid="shipment-import-delivery-suggestion">
                <div className="text-[12px] font-semibold text-teal-100 flex items-center gap-1.5">
                  <CheckCircle size={13} weight="duotone" className="text-teal-300" />
                  Route appears complete — mark this shipment as delivered?
                </div>
                <div className="text-[10px] text-teal-200/80 mt-1">
                  Final timestamp <span className="font-mono">{(replay.delivery_suggestion.final_timestamp || "").slice(0, 19)}</span>.
                  Operational status moves to <span className="font-mono">delivered</span>; lifecycle/notifications/signatures untouched.
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" onClick={handleMarkDelivered} disabled={delivering}
                    className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-[11px] h-7 font-semibold"
                    data-testid="shipment-import-mark-delivered">
                    {delivering ? "Marking…" : "Mark Delivered"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDone} disabled={delivering}
                    className="border-zinc-700 hover:bg-zinc-800 text-[11px] h-7">
                    Keep In Transit
                  </Button>
                </div>
              </div>
            )}

            {!replay.delivery_suggestion?.eligible && (
              <div className="flex items-center justify-end pt-2 border-t border-zinc-800">
                <Button size="sm" onClick={handleDone}
                  className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs"
                  data-testid="shipment-import-done">Done</Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function PreviewTile({ icon, label, value }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-2">
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">
        {icon} {label}
      </div>
      <div className="text-sm font-mono text-zinc-100 truncate">{value}</div>
    </div>
  );
}

function PreviewMini({ label, value, accent = "text-zinc-200" }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-sm font-bold font-mono ${accent}`}>{value}</div>
    </div>
  );
}
